import {
    PHOTOGRAMMETRY_JOB_NAME,
    PIPELINE_INSTANCE_STATUS,
} from "../../../constants";
import { PlayArrow, RestartAlt } from "@mui/icons-material";
import {
    buildUpdatedTranslatedPipeline,
    getRelaunchableJobNames,
} from "../utils/submit";
import { useFetch, useSnackbar } from "../../../hooks";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { LoadingButton } from "@mui/lab";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import { useState } from "react";

SubmitButton.propTypes = {
    pipelineInstance: PropTypes.shape(MODEL_PROPTYPES.PipelineInstance)
        .isRequired,
    mutate: PropTypes.func.isRequired,
    jobNameToModuleVersion: PropTypes.objectOf(
        PropTypes.shape(MODEL_PROPTYPES.ModuleVersion)
    ).isRequired,
};

export function SubmitButton({
    pipelineInstance,
    mutate,
    jobNameToModuleVersion,
}) {
    const { patch, post, put } = useFetch();
    const { openSnackbar } = useSnackbar();
    const { handleSubmit } = useFormContext();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isError = pipelineInstance.status === PIPELINE_INSTANCE_STATUS.ERROR;
    const isFinished =
        pipelineInstance.status === PIPELINE_INSTANCE_STATUS.FINISHED;
    const isPlanned =
        pipelineInstance.status === PIPELINE_INSTANCE_STATUS.PLANNED;

    const jobs = pipelineInstance.translatedPipeline?.spec?.jobs || [];
    const hasPhotogrammetry = jobs.some(
        (job) => job.name === PHOTOGRAMMETRY_JOB_NAME
    );

    const showRelaunch = isError || isFinished;
    if (showRelaunch && !hasPhotogrammetry) return null;

    const onInvalid = (errors) => {
        const messages = collectErrorMessages(errors);
        openSnackbar(
            messages.length > 0
                ? messages.join("; ")
                : "Form has missing or invalid fields",
            "error"
        );
    };

    const handleOpenOperateDialog = handleSubmit(
        () => setIsDialogOpen(true),
        onInvalid
    );

    const handleOperate = handleSubmit(async (formValues) => {
        setIsLoading(true);
        setIsDialogOpen(false);
        try {
            const updated = buildUpdatedTranslatedPipeline(
                pipelineInstance.translatedPipeline,
                formValues,
                jobNameToModuleVersion
            );

            await patch(
                `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}`,
                {
                    body: { translatedPipeline: updated },
                    forwardError: true,
                }
            );

            await post(
                `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}/operate`,
                { forwardError: true }
            );
            openSnackbar("Pipeline operation started successfully", "success");
            mutate();
        } catch (error) {
            openSnackbar(
                error.message || "Failed to operate pipeline",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    }, onInvalid);

    const handleOpenRelaunchDialog = handleSubmit(
        () => setIsDialogOpen(true),
        onInvalid
    );

    // Persist the edited args for the jobs the relaunch will restart
    // (photogrammetry + downstream), then trigger the relaunch. The
    // relaunch endpoint takes no body — it reads args from the
    // pipelineInstance we PATCH here, so the PATCH must precede the PUT.
    // Only the relaunchable jobs are rebuilt; the rest are disabled and
    // therefore absent from formValues.
    const handleRelaunch = handleSubmit(async (formValues) => {
        setIsLoading(true);
        setIsDialogOpen(false);
        try {
            const updated = buildUpdatedTranslatedPipeline(
                pipelineInstance.translatedPipeline,
                formValues,
                jobNameToModuleVersion,
                getRelaunchableJobNames(jobs)
            );

            await patch(
                `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}`,
                {
                    body: { translatedPipeline: updated },
                    forwardError: true,
                }
            );

            await put(
                `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}/relaunch`,
                { forwardError: true }
            );
            openSnackbar("Pipeline relaunched successfully", "success");
            mutate();
        } catch (error) {
            openSnackbar(
                error.message || "Failed to relaunch pipeline",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    }, onInvalid);

    return (
        <>
            {showRelaunch ? (
                <LoadingButton
                    variant="outlined"
                    color={isError ? "error" : "primary"}
                    startIcon={<RestartAlt />}
                    onClick={handleOpenRelaunchDialog}
                    loading={isLoading}
                >
                    Relaunch Photogrammetry
                </LoadingButton>
            ) : (
                <LoadingButton
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handleOpenOperateDialog}
                    loading={isLoading}
                    disabled={
                        !pipelineInstance.translatedPipeline || !isPlanned
                    }
                >
                    Operate
                </LoadingButton>
            )}
            <ConfirmModal
                isOpen={isDialogOpen}
                onCancel={() => setIsDialogOpen(false)}
                onConfirm={showRelaunch ? handleRelaunch : handleOperate}
                title={showRelaunch ? "Relaunch Pipeline" : "Confirm Operation"}
                contentText={
                    showRelaunch
                        ? "Are you sure you want to relaunch the pipeline instance? This will restart the photogrammetry job and all dependent jobs."
                        : "Are you sure you want to operate this pipeline instance? This will start the pipeline execution process."
                }
            />
        </>
    );
}

// Walk react-hook-form's nested errors object and collect every leaf
// `.message` string, since the tree mirrors the form-values shape.
function collectErrorMessages(errors) {
    const messages = [];
    function walk(node) {
        if (!node || typeof node !== "object") return;
        if (typeof node.message === "string" && node.type !== undefined) {
            messages.push(node.message);
            return;
        }
        Object.values(node).forEach(walk);
    }
    walk(errors);
    return messages;
}
